import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    CardContent,
    Container,
    CssBaseline,
    Grid,
    makeStyles,
    TextField,
    Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Alert } from "@material-ui/lab";
import { AutoDisabler, Validate, ValidationGroup } from "mui-validate";
import DeviceDataService from "../../../services/DeviceService";
import DeviceGroupsService from "../../../services/DeviceGroupsService";
import { Snackbar } from "@mui/material";
import AppBarComponent from "../../layout/AppBar";
import Scrollbar from "react-scrollbars-custom";
import clsx from "clsx";
import LocationService from "../../../services/LocationService";
import DeviceAssignment from "./DeviceAssignment";
import Toolbar from "@material-ui/core/Toolbar";
import AppBar from "@material-ui/core/AppBar";
import GroupDeviceTabs from "./GroupDeviceTabs";

const drawerWidth = 280;

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
    },
    toolbar: {
        backgroundColor: "white",
        paddingRight: 24,
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    },
    appBarShift: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    appBarSpacer: theme.mixins.toolbar,
    content: {
        flexGrow: 1,
        height: "100vh",
        overflow: "auto",
    },
    container: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    gridContainer: {
        display: "flex",
        "& > .MuiGrid-item": {
            display: "flex",
        },
    },
    leftColumn: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
    },
    rightColumn: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    card: {
        borderRadius: "4px",
        display: "flex",
        flexDirection: "column",
        flex: 1,
    },
    cardContent: {
        padding: theme.spacing(2),
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: "50px", // Set a fixed minimum height for both cards
        "& .mui-validate__validate-root": {
            width: "95% !important",
        },
    },
    formContainer: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
    },
    buttonWrapper: {
        marginTop: "auto",
        paddingTop: theme.spacing(2),
    },
}));

export default function GroupManagement() {
    const classes = useStyles();
    const [loc] = React.useState(() => {
        const locData = localStorage.getItem("loc");
        return locData ? JSON.parse(locData).id : "";
    });

    // State management
    const [groups, setGroups] = useState([]);
    const [devices, setDevices] = useState([]);
    const [selectedDevices, setSelectedDevices] = useState([]);
    const [selectedFloor, setSelectedFloor] = useState(null);
    const [newGroupName, setNewGroupName] = useState("");
    const [snackbars, setSnackbars] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // First, add these new imports and state variables at the top of your component:
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);

    // Add this new function to fetch locations
    const retrieveLocations = async () => {
        try {
            const response = await LocationService.getAll();
            setLocations(response.data);
        } catch (error) {
            handleShowSnackbar("Грешка при зареждане на локациите", "error");
        }
    };

    // Load initial data
    useEffect(() => {
        const loadInitialData = async () => {
            setIsInitialLoading(true);
            await Promise.all([
                retrieveLocations(),
                retrieveDevices(),
                retrieveGroups()
            ]);
            setIsInitialLoading(false);
        };
        loadInitialData();
    }, []);

    // Fetch devices
    const retrieveDevices = async () => {
        try {
            const response = await DeviceDataService.adminAll();
            setDevices(response.data);
        } catch (error) {
            handleShowSnackbar("Грешка при зареждане на устройствата!", "error");
        }
    };

    // Fetch groups
    const retrieveGroups = async () => {
        try {
            const response = await DeviceGroupsService.getAllAdmin();
            setGroups(response.data);
        } catch (error) {
            handleShowSnackbar("Грешка при зареждане на групите!", "error");
        }
    };

    // Snackbar handling
    const handleShowSnackbar = (message, severity) => {
        const id = new Date().getTime();
        setSnackbars((prev) => [...prev, { id, message, severity }]);
    };

    const handleCloseSnackbar = (id) => {
        setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id));
    };

    // Group creation
    const handleCreateFloor = async () => {
        try {
            setIsLoading(true);
            await DeviceGroupsService.create({
                name: newGroupName
            });
            handleShowSnackbar("Групата е създадена успешно!", "success");
            setNewGroupName(""); // Clear the form after successful creation
            retrieveGroups();
        } catch (error) {
            handleShowSnackbar("Грешка при създаване на групата!", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveDeviceFromGroup = async (groupId, deviceId) => {
        try {
            setIsLoading(true);
            await DeviceGroupsService.bulkRemove(groupId, [deviceId]);
            handleShowSnackbar("Устройството е премахнато успешно!", "success");
            retrieveGroups(); // Refresh the groups list
        } catch (error) {
            console.error("Remove device error:", error);
            handleShowSnackbar("Грешка при премахване на устройството!", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteGroup = async (groupId) => {
        try {
            setIsLoading(true);
            await DeviceGroupsService.remove(groupId);
            handleShowSnackbar("Групата е изтрита успешно!", "success");
            retrieveGroups(); // Refresh the groups list
        } catch (error) {
            console.error("Delete group error:", error);
            handleShowSnackbar("Грешка при изтриване на групата!", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloneGroup = async (group, newName) => {
        try {
            setIsLoading(true);
            const createResponse = await DeviceGroupsService.create({ name: newName });
            const newGroupId = createResponse.data.id;

            if (group.devices && group.devices.length > 0) {
                await DeviceGroupsService.bulkAssign({
                    groupId: newGroupId,
                    deviceIds: group.devices.map((d) => d.id),
                });
            }

            handleShowSnackbar("Групата е клонирана успешно!", "success");
            retrieveGroups();
        } catch (error) {
            console.error("Clone group error:", error);
            handleShowSnackbar("Грешка при клониране на групата!", "error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={classes.root}>
            <CssBaseline />
            <AppBarComponent />
            <main className={classes.content}>
                {snackbars
                    .slice()
                    .reverse()
                    .map((snackbar, index) => (
                        <Snackbar
                            key={snackbar.id}
                            open={true}
                            autoHideDuration={5000}
                            onClose={() => handleCloseSnackbar(snackbar.id)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            style={{ bottom: `${10 + index * 55}px` }}
                        >
                            <Alert
                                elevation={6}
                                variant="filled"
                                severity={snackbar.severity}
                                onClose={() => handleCloseSnackbar(snackbar.id)}
                            >
                                {snackbar.message}
                            </Alert>
                        </Snackbar>
                    ))}
                <Scrollbar id="RSC-Example" style={{ width: "100%", height: "100%" }}>
                    <div className={classes.appBarSpacer} />
                    <Container maxWidth="xl" className={classes.container}>
                        <Grid container spacing={2}>
                            {/* Left Column: Floor Creation Card + DeviceAssignment */}
                            <Grid item xs={12} lg={6}>
                                <div className={classes.leftColumn}>
                                    {/* Floor Creation Card */}
                                    <ValidationGroup>
                                        <Card className={clsx(classes.card)}>
                                            <AppBar position="static" elevation={0} color="primary">
                                                <Toolbar className="px-8">
                                                    <Typography
                                                        variant="subtitle1"
                                                        color="inherit"
                                                        className="flex-1 px-12"
                                                    >
                                                        Създаване на нова група
                                                    </Typography>
                                                </Toolbar>
                                            </AppBar>
                                            <CardContent className={classes.cardContent}>
                                                <div className={classes.formContainer}>
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={12}>
                                                            <Validate name="floorName" required>
                                                                <TextField
                                                                    label="Име на групата"
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                    required
                                                                    value={newGroupName}
                                                                    onChange={(e) =>
                                                                        setNewGroupName(e.target.value)
                                                                    }
                                                                />
                                                            </Validate>
                                                        </Grid>
                                                        <div className={classes.buttonWrapper}>
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent: "flex-start",
                                                                }}
                                                            >
                                                                <AutoDisabler>
                                                                    <Button
                                                                        variant="contained"
                                                                        color="primary"
                                                                        onClick={handleCreateFloor}
                                                                        disabled={isLoading}
                                                                    >
                                                                        Създай група
                                                                    </Button>
                                                                </AutoDisabler>
                                                            </div>
                                                        </div>
                                                    </Grid>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </ValidationGroup>

                                    {/* DeviceAssignment Component */}
                                    <DeviceAssignment
                                        devices={devices}
                                        groups={groups}
                                        onAssignDevices={async (selectedGroup, selectedDevices) => {
                                            try {
                                                setIsLoading(true);

                                                if (!selectedGroup.id) {
                                                    handleShowSnackbar("Невалидена група", "error");
                                                    return;
                                                }

                                                const validDevices = selectedDevices.filter(
                                                    (device) => device && device.id
                                                );
                                                if (validDevices.length === 0) {
                                                    handleShowSnackbar("Невалидни устройства", "error");
                                                    return;
                                                }

                                                const payload = {
                                                    groupId: selectedGroup.id,
                                                    deviceIds: validDevices.map((device) => device.id),
                                                };

                                                await DeviceGroupsService.bulkAssign(payload);
                                                handleShowSnackbar(
                                                    "Успешно зададохте устройствата към групата",
                                                    "success"
                                                );
                                                retrieveGroups();
                                            } catch (error) {
                                                handleShowSnackbar(
                                                    "Грешка при задаването на група",
                                                    "error"
                                                );
                                            } finally {
                                                setIsLoading(false);
                                            }
                                        }}
                                        isLoading={isLoading}
                                    />
                                </div>
                            </Grid>

                            {/* Right Column: GroupDeviceTabs (Full Height) */}
                            <Grid item xs={12} lg={6}>
                                <div className={classes.rightColumn}>
                                    <GroupDeviceTabs
                                        deviceGroups={groups}
                                        devices={devices}
                                        onRemoveDevice={handleRemoveDeviceFromGroup}
                                        onDeleteGroup={handleDeleteGroup}
                                        onCloneGroup={handleCloneGroup}
                                        isLoading={isInitialLoading}
                                    />
                                </div>
                            </Grid>
                        </Grid>

                    </Container>
                </Scrollbar>
            </main>
        </div>
    );
}
